/**
 * Routing, providers, and the guard that stands in front of everything.
 *
 * URL shape is deliberate, because notifications deep-link into it and those
 * links have to survive:
 *
 *   /                                  the reader's semesters
 *   /bulletin                          the activity feed
 *   /settings                          appearance, notifications, devices
 *   /s/:semesterId                     a semester's home
 *   /s/:semesterId/announcements/:id   one announcement
 *   /s/:semesterId/classes/:courseId   one class
 *   /s/:semesterId/coursework/:todoId  one piece of coursework
 *   /s/:semesterId/agenda/:agendaId    one agenda entry
 *   /s/:semesterId/files/:documentId   one document
 *   /admin/…                           the console
 *
 * Every screen below the shell is code-split. The student bundle should never
 * contain the admin console or the editor, and on a phone that is the
 * difference between opening instantly and waiting.
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { SessionProvider, useSession } from './state/SessionProvider';
import { ToastProvider } from './state/ToastProvider';
import { RouteFallback } from './components/ui/States';
import { AppShell } from './components/shell/AppShell';
import { ErrorBoundary } from './components/shell/ErrorBoundary';
import SignIn from './routes/SignIn';

// ── Student & shared screens ─────────────────────────────────────────────────
const Home = lazy(() => import('./routes/Home'));
const Bulletin = lazy(() => import('./routes/Bulletin'));
const Settings = lazy(() => import('./routes/Settings'));
const DegreeProgress = lazy(() => import('./routes/DegreeProgress'));
const Search = lazy(() => import('./routes/Search'));
const NotFound = lazy(() => import('./routes/NotFound'));

const SemesterLayout = lazy(() => import('./routes/semester/SemesterLayout'));
const SemesterHome = lazy(() => import('./routes/semester/SemesterHome'));
const Announcements = lazy(() => import('./routes/semester/Announcements'));
const AnnouncementDetail = lazy(() => import('./routes/semester/AnnouncementDetail'));
const Classes = lazy(() => import('./routes/semester/Classes'));
const ClassDetail = lazy(() => import('./routes/semester/ClassDetail'));
const Coursework = lazy(() => import('./routes/semester/Coursework'));
const CourseworkDetail = lazy(() => import('./routes/semester/CourseworkDetail'));
const SubmissionFile = lazy(() => import('./routes/semester/SubmissionFile'));
const Agenda = lazy(() => import('./routes/semester/Agenda'));
const AgendaDetail = lazy(() => import('./routes/semester/AgendaDetail'));
const Files = lazy(() => import('./routes/semester/Files'));
const FileDetail = lazy(() => import('./routes/semester/FileDetail'));

// ── Admin console ────────────────────────────────────────────────────────────
const AdminLayout = lazy(() => import('./routes/admin/AdminLayout'));
const AdminOverview = lazy(() => import('./routes/admin/AdminOverview'));
const AdminSemesters = lazy(() => import('./routes/admin/AdminSemesters'));
const AdminSemesterDetail = lazy(() => import('./routes/admin/AdminSemesterDetail'));
const AdminPeople = lazy(() => import('./routes/admin/AdminPeople'));
const AdminGroups = lazy(() => import('./routes/admin/AdminGroups'));
const AdminDeliveries = lazy(() => import('./routes/admin/AdminDeliveries'));
const AdminDevices = lazy(() => import('./routes/admin/AdminDevices'));
const AdminActivity = lazy(() => import('./routes/admin/AdminActivity'));
const AdminSearch = lazy(() => import('./routes/admin/AdminSearch'));

export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <ToastProvider>
          <Gate />
        </ToastProvider>
      </SessionProvider>
    </BrowserRouter>
  );
}

/**
 * The authentication boundary.
 *
 * Nothing below it renders — not a layout, not a lazy chunk — until a verified
 * @slr.family account is present. This is convenience and privacy, not
 * security: the Security Rules refuse the data regardless. What it prevents is
 * the far more common failure of a signed-out user staring at an empty,
 * apparently broken application.
 */
function Gate() {
  const { status } = useSession();

  if (status === 'initialising') {
    return (
      <div className="shell">
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '100dvh' }} role="status">
          <span className="spinner" style={{ width: 24, height: 24 }} aria-label="Loading" />
        </div>
      </div>
    );
  }

  if (status !== 'ready') return <SignIn status={status} />;

  return (
    <AppShell>
      <RoutedScreens />
    </AppShell>
  );
}

/**
 * The routed screens, behind an error boundary.
 *
 * Inside the shell rather than around it, so a screen that throws still leaves
 * the header and navigation in place — the reader can move somewhere else
 * instead of being stranded.
 */
function RoutedScreens() {
  const location = useLocation();

  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/bulletin" element={<Bulletin />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/search" element={<Search />} />
          <Route path="/degree-progress" element={<DegreeProgress />} />

          <Route path="/s/:semesterId" element={<SemesterLayout />}>
            <Route index element={<SemesterHome />} />
            <Route path="announcements" element={<Announcements />} />
            <Route path="announcements/:announcementId" element={<AnnouncementDetail />} />
            <Route path="classes" element={<Classes />} />
            <Route path="classes/:courseId" element={<ClassDetail />} />
            <Route path="coursework" element={<Coursework />} />
            <Route path="coursework/:todoId" element={<CourseworkDetail />} />
            {/* Each attached file has its own address, so a completion email
                can link straight to the thing it is about. */}
            <Route
              path="coursework/:todoId/submissions/:fileName"
              element={<SubmissionFile />}
            />
            <Route path="agenda" element={<Agenda />} />
            <Route path="agenda/:agendaId" element={<AgendaDetail />} />
            <Route path="files" element={<Files />} />
            <Route path="files/:documentId" element={<FileDetail />} />
          </Route>

          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminOverview />} />
            <Route path="semesters" element={<AdminSemesters />} />
            <Route path="semesters/:semesterId" element={<AdminSemesterDetail />} />
            <Route path="people" element={<AdminPeople />} />
            <Route path="groups" element={<AdminGroups />} />
            <Route path="deliveries" element={<AdminDeliveries />} />
            <Route path="devices" element={<AdminDevices />} />
            <Route path="activity" element={<AdminActivity />} />
            <Route path="search" element={<AdminSearch />} />
          </Route>

          {/*
            Legacy links. The previous site addressed courses directly; anything
            still pointing there — a bookmark, an old email — lands on the home
            screen rather than a dead end.
          */}
          <Route path="/courses/*" element={<Navigate to="/" replace />} />
          <Route path="/hey" element={<Navigate to="/bulletin" replace />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
